"use client";

import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import type { PublicCreator, PublicTeam } from "@/lib/api";
import { CAPTAIN_HEADSHOTS, DRAFT_ROSTERS } from "@/data/draftRosters";
import RosterComponent from "../RosterComponent";

type LegionCard = {
  key: string;
  name: string;
  faction: "asmodian" | "elyos";
};

const LEGION_CARDS: LegionCard[] = [
  { key: "wakayashi", name: "Team Wakayashi", faction: "asmodian" },
  { key: "stoopzz", name: "Team Stoopzz", faction: "asmodian" },
  { key: "rakin", name: "Team Rakin", faction: "asmodian" },
  { key: "tixinha", name: "Team Tixinha", faction: "elyos" },
  { key: "sonii", name: "Team Sonii", faction: "elyos" },
  { key: "baggins", name: "Team Baggins", faction: "elyos" },
];

export function LegionsSection({
  teams,
  creators,
}: {
  teams: PublicTeam[] | null;
  creators: PublicCreator[] | null;
}) {
  const t = useTranslations("pages.home.cs.legions");

  const [selectedIndex, setSelectedIndex] = useState(2);
  const selectedCard = LEGION_CARDS[selectedIndex];
  const selected = useMemo(() => {
    const key = selectedCard.key;
    return (
      (teams ?? []).find((team) =>
        team.name.toLowerCase().replace(/[^a-z0-9]/g, "").includes(key),
      ) ?? null
    );
  }, [teams, selectedCard.key]);

  const legionRowRef = useRef<HTMLDivElement>(null);

  const selectLegion = useCallback((index: number, smooth = true) => {
  setSelectedIndex(index);

  const row = legionRowRef.current;
  const card = row?.querySelector<HTMLElement>(
    `[data-legion-index="${index}"]`,
  );

  card?.scrollIntoView({
    behavior: smooth ? "smooth" : "auto",
    block: "nearest",
    inline: "center",
  });
}, []);

useEffect(() => {
  const row = legionRowRef.current;

  if (!row) return;

  const mobileQuery = window.matchMedia("(max-width: 1200px)");

  if (!mobileQuery.matches) return;

  // Start with Team Rakin centered.
  const initialCard = row.querySelector<HTMLElement>(
    `[data-legion-index="2"]`,
  );

  requestAnimationFrame(() => {
    initialCard?.scrollIntoView({
      behavior: "auto",
      block: "nearest",
      inline: "center",
    });
  });

  let scrollTimer: ReturnType<typeof setTimeout>;

  const handleScroll = () => {
    clearTimeout(scrollTimer);

    scrollTimer = setTimeout(() => {
      const cards =
        row.querySelectorAll<HTMLElement>("[data-legion-index]");

      const rowRect = row.getBoundingClientRect();
      const rowCenter = rowRect.left + rowRect.width / 2;

      let closestIndex = selectedIndex;
      let closestDistance = Infinity;

      cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;

        const distance = Math.abs(cardCenter - rowCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = Number(card.dataset.legionIndex);
        }
      });

      setSelectedIndex(closestIndex);
    }, 80);
  };

  row.addEventListener("scroll", handleScroll, {
    passive: true,
  });

  return () => {
    clearTimeout(scrollTimer);
    row.removeEventListener("scroll", handleScroll);
  };
}, []);

  const roster = useMemo(() => {
    const picks = DRAFT_ROSTERS[selectedCard.key] ?? [];
    const byName = new Map(
      (creators ?? []).map((creator) => [
        creator.displayName.trim().toLowerCase(),
        creator,
      ]),
    );

    return picks.map((name, index) => {
      const match = byName.get(name.toLowerCase());
      return {
        id: `${selectedCard.key}-${index + 1}-${match?.id ?? name}`,
        displayName: name,
        profileImageUrl:
          index === 0
            ? (CAPTAIN_HEADSHOTS[selectedCard.key] ?? null)
            : (match?.profileImageUrl ?? null),
        isCaptain: index === 0,
      };
    });
  }, [creators, selectedCard.key]);

  return (
    <section className="cs-section cs-legions" id="legions">
      <div className="cs-container">
        <h2 className="cs-title">{t("title")}</h2>

        <div ref={legionRowRef} className="cs-legion-row" role="tablist" aria-label={t("select")}>
          {LEGION_CARDS.map((card, index) => (

            <button
  key={card.key} 
  type="button"
  role="tab"
  data-legion-index={index}
  aria-label={card.name}
  aria-selected={selectedIndex === index}
  className={`cs-legion-card cs-legion-tier-${Math.min(
    Math.abs(index - selectedIndex),

  )} cs-f-${card.faction}${
    selectedIndex === index ? " is-selected" : ""
  }`}
  onClick={() => selectLegion(index)}
>

              <img
  className="cs-legion-card-art cs-legion-card-art-desktop cs-legion-card-art-default"
  src={`/assets/legions/desktop/${card.key}.svg`}
  alt=""
/>

<img
  className="cs-legion-card-art cs-legion-card-art-desktop cs-legion-card-art-active"
  src={`/assets/legions/desktop/${card.key}-active.svg`}
  alt=""
/>

<img
  className="cs-legion-card-art cs-legion-card-art-mobile"
  src={`/assets/legions/mobile/${card.key}.svg`}
  alt=""
/>

{selectedIndex === index ? (
  <img
    className="cs-legion-selected-arrow"
    src="/assets/legion-hover.svg"
    alt=""
  />
) : null}
            </button>
          ))}
        </div>

        <RosterComponent
          key={selectedCard.key}
          team={selected}
          roster={roster}
          faction={selectedCard.faction}
          legionName={selectedCard.name}
        />
      </div>
    </section>
  );
}
